import { useState } from 'react';
import { Row, Col, Card, Badge, Form, Button, Spinner } from 'react-bootstrap';
import { HiOutlineExclamationCircle, HiOutlineChip } from 'react-icons/hi';
import { FaBuilding } from 'react-icons/fa';
import Api from '../../../services/Api';
import { NotifyModal } from '../../../components/NotifyModal/NotifyModal';

const verde = "#1c4220";
const verdeClaro = "#8fad15";

const getSemtEquipments = (unit) =>
  (Array.isArray(unit?.equipments) ? unit.equipments : [])
    .filter(s => String(s).toUpperCase().includes('SEMT'));

const SemtSelector = ({ equipId, semtEquipments, linked, onToggle, allLinkedEquipments = [] }) => {
  const available = semtEquipments
    .filter(s => {
      const serial = typeof s === 'object' ? (s.serialNumber || s.serial || s) : s;
      return !linked.includes(serial) && !allLinkedEquipments.includes(serial);
    })
    .sort((a, b) => {
      const aSerial = typeof a === 'object' ? (a.serialNumber || a.serial || a) : a;
      const bSerial = typeof b === 'object' ? (b.serialNumber || b.serial || b) : b;
      return aSerial.localeCompare(bSerial, undefined, { numeric: true });
    });

  const getEquipmentDisplay = (serial) => {
    const equipment = semtEquipments.find(item => {
      const itemSerial = typeof item === 'object' ? (item.serialNumber || item.serial || item) : item;
      return itemSerial === serial;
    });
    
    if (typeof equipment === 'object' && equipment?.name) {
      return `${serial} - ${equipment.name}`;
    }
    return serial;
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="small fw-bold mb-0" style={{ color: verde }}>
          Equipamentos Vinculados
        </Form.Label>
        <small className="text-muted">
          {linked.length} de {semtEquipments.length} selecionado(s)
        </small>
      </div>

      <Form.Select
        size="sm"
        value=""
        onChange={e => { if (e.target.value) onToggle(equipId, e.target.value, linked); }}
        style={{ borderColor: `${verde}60` }}
        disabled={available.length === 0}
      >
        <option value="">
          {available.length === 0 ? 'Todos os equipamentos vinculados' : 'Selecione para adicionar...'}
        </option>
        {available.map(item => {
          const isObject = typeof item === 'object' && item !== null;
          const serial = isObject ? (item.serialNumber || item.serial || item) : item;
          const name = isObject ? item.name : null;
          const displayText = name ? `${serial} - ${name}` : serial;
          
          return (
            <option key={serial} value={serial}>{displayText}</option>
          );
        })}
      </Form.Select>

      {linked.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mt-2">
          {linked.map(serial => (
            <Badge
              key={serial}
              bg="success"
              className="d-flex align-items-center"
              title={getEquipmentDisplay(serial)}
              style={{ fontSize: '0.75rem' }}
            >
              <span style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {getEquipmentDisplay(serial)}
              </span>
              <span
                onClick={() => onToggle(equipId, serial, linked)}
                title="Remover"
                style={{ cursor: 'pointer', fontWeight: 'bold', marginLeft: '6px', lineHeight: 1 }}
              >
                ×
              </span>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

const RotationConfigForm = ({ user, unit, alarmEquipments, onAlarmUpdate }) => {
  const [editState, setEditState] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [successId, setSuccessId] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalType, setModalType] = useState({ color: '', status: '' });

  const semtEquipments = getSemtEquipments(unit);

  const showError = (msg) => {
    setModalType({ color: 'red', status: msg });
    setModalShow(true);
  };

  const getField = (equip, field) =>
    editState[equip._id]?.[field] !== undefined
      ? editState[equip._id][field]
      : equip[field];

  const handleChange = (equipId, field, value) => {
    setEditState(prev => ({
      ...prev,
      [equipId]: { ...prev[equipId], [field]: value }
    }));
  };

  const toggleSemt = (equipId, serial, currentLinked) => {
    const linked = Array.isArray(currentLinked) ? currentLinked : [];
    const updated = linked.includes(serial)
      ? linked.filter(s => s !== serial)
      : [...linked, serial];
    setEditState(prev => ({
      ...prev,
      [equipId]: { ...prev[equipId], equipments: updated }
    }));
  };

  const handleSave = async (equip) => {
    setSavingId(equip._id);
    try {
      const changes = editState[equip._id] || {};
      const payload = {
        description: changes.description ?? equip.description ?? '',
        equipments: changes.equipments ?? equip.equipments ?? [],
      };

      const { data: updated } = await Api.put(`/rotation/v1/${encodeURIComponent(equip.serialNumber)}`, payload);

      onAlarmUpdate?.(updated);
      setEditState(prev => ({ ...prev, [equip._id]: undefined }));
      setSuccessId(equip._id);
      setTimeout(() => setSuccessId(null), 2000);
    } catch (e) {
      showError('Erro ao salvar configuração do rodízio!');
      console.error(e);
    }
    setSavingId(null);
  };

  return (
    <div>
      <NotifyModal type={modalType} show={modalShow} onHide={() => setModalShow(false)} />

      {/* Cabeçalho da unidade */}
      <Card className="border-0 shadow-sm mb-2">
        <Card.Header>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <FaBuilding style={{ color: 'white' }} size={20} />
              <h5 className="mb-0 fw-semibold text-white">
                {user?.hideSensitiveInfo ? '*****' : unit?.name?.toUpperCase() || 'Empresa sem nome'}
              </h5>
            </div>
            <Badge pill bg="success" className="px-3 py-2" style={{ fontSize: '0.85rem' }}>
              {alarmEquipments.length} {alarmEquipments.length === 1 ? 'rodízio' : 'rodízios'}
            </Badge>
          </div>
        </Card.Header>
      </Card>

      {/* Cards dos equipamentos RODZ */}
      {alarmEquipments.length === 0 ? (
        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
          <HiOutlineExclamationCircle size={52} style={{ color: '#6c757d', opacity: 0.4 }} />
          <p className="mt-3 text-muted">Nenhum equipamento RODZ encontrado nesta unidade</p>
        </div>
      ) : (
        <Row className="g-2">
          {(() => {
            const allLinkedEquipments = alarmEquipments.reduce((acc, equip) => {
              const linked = getField(equip, 'equipments') ?? equip.equipments ?? [];
              return [...acc, ...(Array.isArray(linked) ? linked : [])];
            }, []);

            return alarmEquipments.map((equip) => {
              const isSaving = savingId === equip._id;
              const isSuccess = successId === equip._id;
              const rotationOn = String(equip.rotationStatus).toUpperCase() === 'ON';
              const currentLinked = Array.isArray(getField(equip, 'equipments') ?? equip.equipments)
                ? (getField(equip, 'equipments') ?? equip.equipments)
                : [];

              return (
              <Col xs={12} lg={6} key={equip._id}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center py-2 px-3">
                    <div className="d-flex align-items-center gap-2">
                      <HiOutlineChip size={20} style={{ color: '#fff' }} />
                      <span className="fw-bold" style={{ color: '#fff' }}>
                        {equip.serialNumber}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-muted">FW: {equip.fw || '--'}</small>
                      <Badge bg={rotationOn ? 'danger' : 'secondary'} style={{ fontSize: '0.75rem' }}>
                        {rotationOn ? 'ATIVO' : 'INATIVO'}
                      </Badge>
                    </div>
                  </Card.Header>

                  <Card.Body className="px-3 py-3">
                    <Form>
                      {/* Descrição */}
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold mb-1" style={{ color: verde }}>
                          Descrição
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Descrição do rodízio..."
                          value={getField(equip, 'description') ?? ''}
                          onChange={e => handleChange(equip._id, 'description', e.target.value)}
                          style={{ borderColor: `${verde}60` }}
                        />
                      </Form.Group>

                      {/* Equipamentos SEMT vinculados */}
                      {semtEquipments.length > 0 && (
                        <SemtSelector
                          equipId={equip._id}
                          semtEquipments={semtEquipments}
                          linked={currentLinked}
                          onToggle={toggleSemt}
                          allLinkedEquipments={allLinkedEquipments.filter(s => !currentLinked.includes(s))}
                        />
                      )}
                    </Form>
                  </Card.Body>

                  <Card.Footer className="bg-transparent border-0 px-3 pb-3 pt-1">
                    <Button
                      size="sm"
                      className="w-100 rounded-pill py-2"
                      disabled={isSaving}
                      onClick={() => handleSave(equip)}
                      style={{
                        backgroundColor: isSuccess ? verdeClaro : verde,
                        borderColor: isSuccess ? verdeClaro : verde,
                        color: isSuccess ? verde : 'white',
                        fontWeight: '600',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isSaving ? (
                        <><Spinner size="sm" className="me-2" />Salvando...</>
                      ) : isSuccess ? (
                        <>✓ Salvo</>
                      ) : (
                        <>Salvar</>
                      )}
                    </Button>
                  </Card.Footer>
                </Card>
              </Col>
            );
            });
          })()}
        </Row>
      )}
    </div>
  );
};

export default RotationConfigForm;
